import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Search, AlertTriangle, CheckCircle, Clock, XCircle, Eye, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

import type { Tables } from '@/integrations/supabase/types';

type Scan = Tables<'scans'>;

interface ScanHistoryProps {
  organizationId: string;
}

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    variant: 'secondary' as const,
    className: 'bg-muted text-muted-foreground',
  },
  processing: {
    label: 'Processing',
    icon: RefreshCw,
    variant: 'secondary' as const,
    className: 'bg-primary/10 text-primary',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    variant: 'secondary' as const,
    className: 'bg-success/10 text-success',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'destructive' as const,
    className: 'bg-destructive/10 text-destructive',
  },
};

export function ScanHistory({ organizationId }: ScanHistoryProps) {
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
  const [totalScans, setTotalScans] = useState(0);

  const fetchScans = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('scans')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter as Scan['status']);
      }

      if (searchQuery) {
        query = query.or(`client_reference.ilike.%${searchQuery}%,source_domain.ilike.%${searchQuery}%`);
      }

      const { data, error, count } = await query.limit(50);

      if (error) throw error;
      setScans(data || []);
      setTotalScans(count || 0);
    } catch (error) {
      console.error('Error fetching scans:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScans();
  }, [organizationId, statusFilter, searchQuery]);

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('scans-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scans',
          filter: `organization_id=eq.${organizationId}`,
        },
        () => {
          fetchScans();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId]);

  const StatusBadge = ({ status }: { status: Scan['status'] }) => {
    const config = statusConfig[status];
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className} flex items-center gap-1.5`}>
        <Icon className={`h-3 w-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by reference or domain..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={fetchScans} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing {scans.length} of {totalScans} scans
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : scans.length === 0 ? (
        <div className="glass-panel rounded-xl p-12 text-center">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No scans found</h3>
          <p className="text-muted-foreground">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Start analyzing credit reports to see results here'}
          </p>
        </div>
      ) : (
        <div className="glass-panel rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-center">Violations</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scans.map((scan) => (
                <TableRow key={scan.id}>
                  <TableCell>
                    <StatusBadge status={scan.status} />
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {scan.client_reference || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {scan.source_domain || '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {scan.status === 'completed' ? (
                      <span className={`font-semibold ${
                        (scan.violation_count || 0) > 0 
                          ? 'text-warning' 
                          : 'text-success'
                      }`}>
                        {scan.violation_count || 0}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {format(new Date(scan.created_at), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {scan.completed_at
                      ? format(new Date(scan.completed_at), 'MMM d, yyyy HH:mm')
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedScan(scan)}
                      disabled={scan.status !== 'completed'}
                      className="gap-1.5"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Results Dialog */}
      <Dialog open={!!selectedScan} onOpenChange={() => setSelectedScan(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Scan Results</DialogTitle>
            <DialogDescription>
              {selectedScan?.client_reference && (
                <span className="font-mono">Reference: {selectedScan.client_reference}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {selectedScan?.results ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="px-4 py-2 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Violations Found</p>
                    <p className={`text-2xl font-bold ${
                      (selectedScan.violation_count || 0) > 0 
                        ? 'text-warning' 
                        : 'text-success'
                    }`}>
                      {selectedScan.violation_count || 0}
                    </p>
                  </div>
                  
                  <div className="px-4 py-2 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedScan.completed_at
                        ? format(new Date(selectedScan.completed_at), 'MMM d, yyyy HH:mm')
                        : '—'}
                    </p>
                  </div>
                </div>
                
                <div className="rounded-lg border border-border bg-muted/50 p-4">
                  <pre className="text-sm text-foreground whitespace-pre-wrap overflow-auto">
                    {JSON.stringify(selectedScan.results, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No results available for this scan.
              </p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
